import { useCallback, useEffect, useState } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import { useEventBus } from '../hooks/useEventBus';
import type { ApiData, BroadcastAnnounce } from '../../core/types';
import styles from './Announcement.module.css';

interface Countdown {
  hours: number;
  minutes: number;
  seconds: number;
}

interface StreamerInfo {
  name: string;
  avatar?: string;
}

function getCountdown(targetDateStr: string): Countdown | null {
  const target = new Date(targetDateStr.replace(/-/g, '/'));
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return null;

  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600) + days * 24;
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { hours, minutes, seconds };
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

const MONTHS_RU_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatBroadcastDate(targetDateStr: string): string {
  const date = new Date(targetDateStr.replace(/-/g, '/'));
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false });
  const day = date.getDate();
  const month = MONTHS_RU_GENITIVE[date.getMonth()];
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'long' });
  return `${time}, ${day} ${month}, ${weekday}`;
}

interface OrientationState {
  logo: string;
  isPortrait: boolean;
  width: number;
  height: number;
}

export function Announcement() {
  const { store, eventBus } = usePlayerContext();
  const announce = useStore(store, useCallback((s) => s.announce, []));
  const isOnline = useStore(store, useCallback((s) => s.isOnline, []));
  const [countdown, setCountdown] = useState<Countdown | null>(null);
  const [streamerInfo, setStreamerInfo] = useState<StreamerInfo | null>(null);
  const [orientationState, setOrientationState] = useState<OrientationState | null>(null);

  // Derived synchronously — null while detecting (shows blur only), true/false once known
  const logo = announce?.broadcast_logo ?? '';
  const isPortrait =
    orientationState !== null && orientationState.logo === logo
      ? orientationState.isPortrait
      : null;

  // Show/hide based on api:data
  const handleApiData = useCallback(
    (data: ApiData) => {
      const broadcast = data.broadcast;
      const isAnnounce =
        broadcast &&
        !Array.isArray(broadcast) &&
        (broadcast as BroadcastAnnounce).broadcast_start;

      if (isAnnounce && data.channel_status !== 'online') {
        store.setState({ announce: broadcast as BroadcastAnnounce });
        setStreamerInfo({ name: data.streamer_name, avatar: data.streamer_avatar });
      } else {
        store.setState({ announce: null });
      }
    },
    [store],
  );
  useEventBus(eventBus, 'api:data', handleApiData);

  // Hide on playback start
  const handlePlay = useCallback(() => {
    store.setState({ announce: null });
  }, [store]);
  useEventBus(eventBus, 'play', handlePlay);

  // Detect broadcast_logo orientation via Image() preload — no DOM element needed
  useEffect(() => {
    const logo = announce?.broadcast_logo ?? '';
    if (!logo) {
      setOrientationState({ logo: '', isPortrait: false, width: 1, height: 1 });
      return;
    }
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) {
        setOrientationState({
          logo,
          isPortrait: img.naturalHeight > img.naturalWidth,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setOrientationState({ logo, isPortrait: false, width: 0, height: 0 }); // default to landscape on error
      }
    };
    img.src = logo;
    return () => {
      cancelled = true;
    };
  }, [announce?.broadcast_logo]);

  // Countdown interval — starts when announce is set, cleans up on clear or unmount
  useEffect(() => {
    if (!announce) {
      setCountdown(null);
      return;
    }

    const tick = () => {
      const next = getCountdown(announce.broadcast_start);
      if (!next) {
        store.setState({ announce: null });
      } else {
        setCountdown(next);
      }
    };

    tick(); // immediate first render
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [announce, store]);

  if (!announce || isOnline) return null;

  const bgStyle = logo ? { backgroundImage: `url('${logo}')` } : {};
  const usePortraitLayout = isPortrait === true;
  const imageWrapperStyle =
    orientationState?.logo === logo && orientationState.width && orientationState.height
      ? { aspectRatio: `${orientationState.width} / ${orientationState.height}` }
      : undefined;

  // Shared content fragments — reused in both layouts
  const dateRowContent = (
    <>
      <span className={styles.dateText}>{formatBroadcastDate(announce.broadcast_start)}</span>
      <span className={styles.dateSep}>/</span>
      {countdown && (
        <span className={styles.countdownText} aria-live="polite" aria-atomic="true">
          {pad(countdown.hours)}:{pad(countdown.minutes)}:{pad(countdown.seconds)}
        </span>
      )}
    </>
  );

  const streamerRowContent = (
    <>
      {streamerInfo && (
        <div className={styles.streamerInfo}>
          {streamerInfo.avatar && (
            <img
              src={streamerInfo.avatar}
              alt={streamerInfo.name}
              className={styles.avatar}
            />
          )}
          <span className={styles.streamerName}>{streamerInfo.name}</span>
        </div>
      )}
      <span className={styles.announced}>анонсировал стрим</span>
      {announce.broadcast_games && (
        <span className={styles.gamePill}>{announce.broadcast_games}</span>
      )}
    </>
  );

  return (
    <div className={styles.overlay}>
      {logo && <div className={styles.blurred} style={bgStyle} aria-hidden="true" />}

      {/* Show content only once orientation is known — avoids layout flash */}
      {isPortrait !== null && (
        usePortraitLayout ? (
          /* Layout 2: portrait image — side-by-side (image left, info right) */
          <div className={styles.innerRow}>
            {logo && (
              <div className={styles.imageWrapperPortrait} style={imageWrapperStyle}>
                <img
                  src={logo}
                  alt={announce.broadcast_title}
                  className={styles.image}
                  role="img"
                />
              </div>
            )}
            <div className={styles.infoPortrait}>
              <div className={styles.dateRowPortrait}>{dateRowContent}</div>
              <p className={styles.titlePortrait}>{announce.broadcast_title}</p>
              <div className={styles.streamerRowLeft}>{streamerRowContent}</div>
            </div>
          </div>
        ) : (
          /* Layout 1: landscape image — stacked centered */
          <div className={styles.inner}>
            {logo && (
              <div className={styles.imageWrapper} style={imageWrapperStyle}>
                <img
                  src={logo}
                  alt={announce.broadcast_title}
                  className={styles.image}
                  role="img"
                />
              </div>
            )}
            <div className={styles.info}>
              <div className={styles.dateRow}>{dateRowContent}</div>
              <p className={styles.title}>{announce.broadcast_title}</p>
              <div className={styles.streamerRow}>{streamerRowContent}</div>
            </div>
          </div>
        )
      )}
    </div>
  );
}
